import {
  canEditBoard,
  LIMITS,
  MINDMAP_LIMITS,
  STATUS_COLOURS,
  STATUS_LABELS,
  STATUSES,
  CARD_COLOUR_HEX,
  CARD_COLOUR_LABELS,
  CARD_COLOURS,
  findMindmapNode,
  mergeLabels,
  mindmapNodes,
  nodeLabels,
  type ItemStatus,
  type MindmapLinks,
  type MindmapNode,
  type NodeCore,
  type ProjectLabels,
} from '@blueprint/domain'
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { AppVersion } from '../../components/AppVersion.js'
import { Dialog } from '../../components/Dialog.js'
import { Button, ColourSwatches, InlineTitle, TextArea } from '../../components/controls.js'
import { IconPicker, NodeCoreFields, useProjectLabels } from '../../components/ItemFields.js'
import { LinkSyncToggles } from '../../app/FooterBar.js'
import { FeedbackTab } from '../feedback/FeedbackTab.js'
import { UserMenu } from '../../app/UserMenu.js'
import { BrandLockup } from '../../components/BrandLockup.js'
import { getRepository, useBoardStore } from '../../state/board-store.js'
import { useMindmapStore } from '../../state/mindmap-store.js'
import { useSessionStore } from '../../state/session-store.js'
import { useThemeStore } from '../../state/theme-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { MindmapEngine } from './engine.js'
import { GenerateStorymapDialog } from './GenerateStorymapDialog.js'
import { download, exportJson, exportMarkdown, fileSlug, readMindmapFile } from './mindmap-files.js'
import styles from './MindmapView.module.css'

interface Menu {
  nodeId: string
  x: number
  y: number
}

/** The mindmap page: top bar and toolbar, the canvas, and the footer with the save state. */
export function MindmapView() {
  const phase = useMindmapStore((state) => state.phase)
  const name = useMindmapStore((state) => state.name)
  const root = useMindmapStore((state) => state.root)
  const access = useMindmapStore((state) => state.access)
  const mindmapId = useMindmapStore((state) => state.id)
  const focusNodeId = useMindmapStore((state) => state.focusNodeId)
  const loads = useMindmapStore((state) => state.loads)
  const setView = useUiStore((state) => state.setView)
  const requestConfirm = useUiStore((state) => state.requestConfirm)
  const editable = canEditBoard(access)
  // During a Teamcentric Accounts trial no data leaves the tenant: no JSON, Markdown or PNG exports.
  const trial = useSessionStore((state) => state.trial)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const minimapRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<MindmapEngine | null>(null)
  const loadedId = useRef<number | null>(null)
  const importInput = useRef<HTMLInputElement>(null)
  const importTarget = useRef<string | null>(null)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [history, setHistory] = useState({ undo: false, redo: false })
  const [filter, setFilter] = useState<ItemStatus | ''>('')
  const [menu, setMenu] = useState<Menu | null>(null)
  const [fileMenu, setFileMenu] = useState(false)
  const [storymapMenu, setStorymapMenu] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [propertiesFor, setPropertiesFor] = useState<string | null>(null)
  const [colourFor, setColourFor] = useState<string | null>(null)
  const [iconFor, setIconFor] = useState<string | null>(null)
  const [generateFor, setGenerateFor] = useState<string | null>(null)
  const [linksFor, setLinksFor] = useState<{ nodeId: string; x: number; y: number } | null>(null)
  const [links, setLinks] = useState<MindmapLinks>({ boards: [], cards: [] })
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showToast = useCallback((message: string) => {
    setToast(message)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 2500)
  }, [])

  const confirmDelete = useCallback(
    (id: string) => {
      const engine = engineRef.current
      const node = engine?.getNode(id)
      if (!engine || !node) return
      let count = 0
      const walk = (current: MindmapNode) => {
        count += current.children.length
        current.children.forEach(walk)
      }
      walk(node)
      requestConfirm({
        title: 'Delete node',
        message: `Delete "${node.title}"?`,
        consequence: count > 0 ? `This will also delete ${count} child node${count === 1 ? '' : 's'}.` : undefined,
        confirmLabel: 'Delete',
        undoable: true,
        onConfirm: () => engine.deleteNode(id),
      })
    },
    [requestConfirm],
  )

  // The engine lives as long as the page; callbacks reach React through refs and setters.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const engine = new MindmapEngine(canvas, minimapRef.current, {
      onCommit: (next) => useMindmapStore.getState().change(next),
      onSelect: setSelectedId,
      onContextMenu: (id, x, y) => {
        setColourFor(null)
        setMenu({ nodeId: id ?? engine.getRoot()?.id ?? '', x, y })
      },
      onEditText: (id) => setEditing(id),
      onDeleteRequest: (id) => confirmDelete(id),
      onLinks: (id, x, y) => setLinksFor({ nodeId: id, x, y }),
      onView: setZoom,
      onToast: showToast,
      onHistory: (undo, redo) => setHistory({ undo, redo }),
    })
    engineRef.current = engine
    return () => {
      engine.destroy()
      engineRef.current = null
      loadedId.current = null
    }
  }, [confirmDelete, showToast])

  // Each load from the server resets the view; our own saves are not loads, so the engine
  // keeps its tree and its undo history through autosave.
  useEffect(() => {
    const engine = engineRef.current
    if (!engine || !root || phase !== 'ready') return
    if (loadedId.current !== loads) {
      loadedId.current = loads
      engine.load(root, { reset: true })
    }
  }, [root, phase, loads])

  useEffect(() => {
    engineRef.current?.setReadOnly(!editable)
  }, [editable])

  useEffect(() => {
    engineRef.current?.setFilter(filter)
  }, [filter])

  useEffect(() => {
    if (!focusNodeId || phase !== 'ready' || loadedId.current !== loads) return
    engineRef.current?.focusNode(focusNodeId)
    useMindmapStore.getState().clearFocus()
  }, [focusNodeId, phase, loads])

  // The canvas reads the theme tokens; repaint once the new ones are applied.
  useEffect(() => {
    const refresh = () => requestAnimationFrame(() => engineRef.current?.refreshTheme())
    const unsubscribe = useThemeStore.subscribe(refresh)
    const media = window.matchMedia?.('(prefers-color-scheme: dark)')
    media?.addEventListener?.('change', refresh)
    return () => {
      unsubscribe()
      media?.removeEventListener?.('change', refresh)
    }
  }, [])

  const refreshLinks = useCallback(async () => {
    if (!mindmapId) return
    try {
      const next = await getRepository().listMindmapLinks(mindmapId)
      setLinks(next)
      const counts = new Map<string, number>()
      for (const card of next.cards) counts.set(card.nodeId, (counts.get(card.nodeId) ?? 0) + 1)
      engineRef.current?.setLinkCounts(counts)
    } catch {
      // The badges are a convenience; the map works without them.
    }
  }, [mindmapId])

  useEffect(() => {
    if (phase === 'ready') void refreshLinks()
  }, [phase, refreshLinks])

  // The legacy app's keyboard shortcuts, while no text field, menu or dialog has the keys.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && (target.closest('input, textarea, select, [contenteditable="true"]') || target.closest('dialog'))) return
      if (document.querySelector('dialog[open]') || useUiStore.getState().confirm) return
      if (event.key === 'Escape') {
        setMenu(null)
        setColourFor(null)
        setFileMenu(false)
        setLinksFor(null)
        setStorymapMenu(false)
        return
      }
      if (editing || noteFor) return
      if (engineRef.current?.handleKey(event)) event.preventDefault()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [editing, noteFor])

  // Any click outside a popup closes it.
  useEffect(() => {
    if (!menu && !colourFor && !iconFor && !fileMenu && !linksFor && !storymapMenu) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-popup]')) return
      setMenu(null)
      setColourFor(null)
      setIconFor(null)
      setFileMenu(false)
      setLinksFor(null)
      setStorymapMenu(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [menu, colourFor, iconFor, fileMenu, linksFor, storymapMenu])

  /** Saves the mindmap first, then shows the storymap, at one of its cards when given. */
  const openStorymap = async (boardId: string, cardId?: string) => {
    await useMindmapStore.getState().leave()
    await useBoardStore.getState().openBoard(boardId)
    if (cardId) useUiStore.getState().setFocusCardId(cardId)
    setView('board')
  }

  const back = async () => {
    await useMindmapStore.getState().leave()
    void useBoardStore.getState().showBoardsPage({})
    setView('boards')
  }

  const engine = engineRef.current
  const selected = selectedId ? (engine?.getNode(selectedId) ?? null) : null
  const isRootSelected = selectedId !== null && engine?.isRoot(selectedId) === true

  const importAsBranch = (nodeId: string) => {
    importTarget.current = nodeId
    importInput.current?.click()
  }

  const onImportFile = async (file: File | undefined) => {
    const targetId = importTarget.current
    importTarget.current = null
    if (!file || !targetId || !engineRef.current) return
    try {
      const imported = await readMindmapFile(file)
      const current = engineRef.current.getRoot()
      if (!current) return
      const next = structuredClone(current)
      const target = findMindmapNode(next, targetId)
      if (!target) return
      target.children.push(imported.root)
      target.collapsed = false
      let count = 0
      const walk = (node: MindmapNode) => {
        count += 1
        node.children.forEach(walk)
      }
      walk(next)
      if (count > MINDMAP_LIMITS.nodes) throw new Error(`A mindmap is limited to ${MINDMAP_LIMITS.nodes} nodes.`)
      engineRef.current.load(next, { reset: false })
      useMindmapStore.getState().change(next)
      showToast(`Imported "${imported.name}" under "${target.title}"`)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'The file could not be imported.')
    }
  }

  const exportPng = async () => {
    const blob = await engineRef.current?.toPng()
    if (blob) download(blob, `${fileSlug(name)}.png`)
  }

  if (phase === 'missing' || phase === 'error') {
    return (
      <div className={styles.page}>
        <section className={styles.state}>
          <h2>{phase === 'missing' ? 'That mindmap no longer exists' : 'The mindmap could not be loaded'}</h2>
          <Button variant="primary" onClick={() => void back()}>
            Back to boards
          </Button>
        </section>
      </div>
    )
  }

  const menuNode = menu ? engine?.getNode(menu.nodeId) : null
  const menuIsRoot = menu ? engine?.isRoot(menu.nodeId) === true : false

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <div className={styles.row}>
          <button type="button" className={styles.brand} onClick={() => void back()} title="Back to all boards">
            <img className={styles.logo} src="/logo-mark.png" alt="" width={40} height={21} />
            <BrandLockup />
          </button>
          <span className={styles.kind}>Mindmap</span>
          {editable ? (
            <span className={styles.name}>
              <InlineTitle
                value={name}
                label="Mindmap name"
                maxLength={LIMITS.title}
                onCommit={(next) => {
                  const current = engineRef.current?.getRoot()
                  if (current) useMindmapStore.getState().change(current, next)
                }}
              />
            </span>
          ) : (
            <span className={styles.name}>{name} · view only</span>
          )}
          <span className={styles.spacer} />
          <Button onClick={() => useUiStore.getState().setSwitcherOpen(true)} disabled={!mindmapId}>
            Switch…
          </Button>
          {editable ? (
            <Button onClick={() => root && setGenerateFor(root.id)} disabled={!root}>
              Create storymap…
            </Button>
          ) : null}
          {links.boards.length > 0 ? (
            <span className={styles.popupWrap} data-popup>
              <Button
                aria-haspopup="menu"
                aria-expanded={storymapMenu}
                title="Open a storymap generated from this mindmap"
                onClick={() => setStorymapMenu((open) => !open)}
              >
                Open storymap ▾
              </Button>
              {storymapMenu ? (
                <div className={styles.dropdown} role="menu">
                  {links.boards.map((board) => {
                    const branch = engineRef.current?.getNode(board.nodeId)
                    return (
                      <MenuItem
                        key={board.id}
                        onClick={() => {
                          setStorymapMenu(false)
                          void openStorymap(board.id)
                        }}
                      >
                        {board.name}
                        {branch && branch.id !== root?.id ? <span className={styles.muted}> · {branch.title}</span> : null}
                      </MenuItem>
                    )
                  })}
                </div>
              ) : null}
            </span>
          ) : null}
          <span className={styles.popupWrap} data-popup>
            <Button aria-haspopup="menu" aria-expanded={fileMenu} onClick={() => setFileMenu((open) => !open)}>
              Import / export ▾
            </Button>
            {fileMenu ? (
              <div className={styles.dropdown} role="menu">
                {editable ? (
                  <MenuItem
                    onClick={() => {
                      setFileMenu(false)
                      importAsBranch(selectedId ?? root?.id ?? '')
                    }}
                  >
                    Import as branch of {selected ? `"${selected.title}"` : 'the root'}…
                  </MenuItem>
                ) : null}
                {trial ? null : (
                  <>
                    <MenuItem
                      onClick={() => {
                        setFileMenu(false)
                        const current = engineRef.current?.getRoot()
                        if (current) exportJson(current, name)
                      }}
                    >
                      Export mindmap (JSON)
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setFileMenu(false)
                        const current = engineRef.current?.getRoot()
                        if (current) exportMarkdown(current, name)
                      }}
                    >
                      Export outline (Markdown)
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setFileMenu(false)
                        void exportPng()
                      }}
                    >
                      Export image (PNG)
                    </MenuItem>
                  </>
                )}
              </div>
            ) : null}
          </span>
          <UserMenu />
        </div>
        <div className={styles.row}>
          {editable ? (
            <>
              <Button onClick={() => engine?.addChild(selectedId ?? undefined)} title="Add child (Tab)">
                + Child
              </Button>
              <Button onClick={() => engine?.addSibling()} disabled={!selected || isRootSelected} title="Add sibling (Enter)">
                + Sibling
              </Button>
              <Button
                onClick={() => selectedId && confirmDelete(selectedId)}
                disabled={!selected || isRootSelected}
                title="Delete (Del)"
              >
                Delete
              </Button>
              <span className={styles.separator} />
            </>
          ) : null}
          <Button onClick={() => engine?.toggleCollapse()} disabled={!selected?.children.length} title="Collapse / expand (Space)">
            Toggle
          </Button>
          <Button onClick={() => engine?.expandAll()}>Expand all</Button>
          <Button onClick={() => engine?.collapseAllChildren()}>Collapse all</Button>
          {editable ? (
            <>
              <span className={styles.separator} />
              <Button onClick={() => engine?.undo()} disabled={!history.undo} title="Undo (Ctrl+Z)">
                Undo
              </Button>
              <Button onClick={() => engine?.redo()} disabled={!history.redo} title="Redo (Ctrl+Shift+Z)">
                Redo
              </Button>
            </>
          ) : null}
          <span className={styles.separator} />
          <Button onClick={() => engine?.zoomOut()} aria-label="Zoom out" title="Zoom out (Ctrl+-)">
            −
          </Button>
          <span className={styles.zoom}>{Math.round(zoom * 100)}%</span>
          <Button onClick={() => engine?.zoomIn()} aria-label="Zoom in" title="Zoom in (Ctrl+=)">
            +
          </Button>
          <Button onClick={() => engine?.fitView()} title="Fit the whole map in view">
            Fit
          </Button>
          <span className={styles.separator} />
          <label className={styles.inline}>
            <span className="visually-hidden">Filter nodes by status</span>
            <select value={filter} onChange={(event) => setFilter(event.target.value as ItemStatus | '')} title="Filter nodes by status">
              <option value="">All nodes</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <div className={styles.canvasHost}>
        <canvas ref={canvasRef} className={styles.canvas} tabIndex={0} aria-label="Mindmap canvas" data-testid="mindmap-canvas" />
        <canvas ref={minimapRef} className={styles.minimap} aria-hidden="true" />
        {phase === 'loading' ? <div className={styles.loading}>Loading mindmap…</div> : null}

        {editing && engine ? (
          <InlineNodeEditor
            key={editing}
            rect={engine.nodeScreenRect(editing)}
            value={engine.getNode(editing)?.title ?? ''}
            onDone={(text) => {
              if (text !== null) engine.setTitle(editing, text)
              setEditing(null)
              canvasRef.current?.focus()
            }}
          />
        ) : null}

        {colourFor && engine ? (
          <div
            className={styles.swatches}
            data-popup
            style={popupPosition(engine.nodeScreenRect(colourFor))}
            role="dialog"
            aria-label="Branch colour"
          >
            <ColourSwatches
              label="Branch colour"
              options={MINDMAP_COLOUR_OPTIONS}
              value={engine.getNode(colourFor)?.colour ?? null}
              onPick={(swatch) => {
                engine.setColour(colourFor, swatch)
                setColourFor(null)
              }}
            />
          </div>
        ) : null}

        {iconFor && engine ? (
          <div
            className={styles.swatches}
            data-popup
            style={popupPosition(engine.nodeScreenRect(iconFor))}
            role="dialog"
            aria-label="Node icon"
          >
            <IconPicker
              value={engine.getNode(iconFor)?.icon ?? null}
              onPick={(icon) => {
                engine.setIcon(iconFor, icon)
                setIconFor(null)
              }}
            />
          </div>
        ) : null}
      </div>

      {menu && menuNode && engine ? (
        <FloatingMenu x={menu.x} y={menu.y}>
          {editable ? (
            <>
              <MenuItem shortcut="Tab" onClick={() => (setMenu(null), engine.addChild(menuNode.id))}>
                Add child node
              </MenuItem>
              {!menuIsRoot ? (
                <MenuItem shortcut="Enter" onClick={() => (setMenu(null), engine.addSibling(menuNode.id))}>
                  Add sibling node
                </MenuItem>
              ) : null}
              <MenuSeparator />
              <MenuItem shortcut="F2" onClick={() => (setMenu(null), setEditing(menuNode.id))}>
                Edit label
              </MenuItem>
              <MenuItem onClick={() => (setMenu(null), setColourFor(menuNode.id))}>Change branch colour</MenuItem>
              <MenuItem onClick={() => (setMenu(null), setIconFor(menuNode.id))}>{menuNode.icon ? 'Change icon' : 'Add icon'}</MenuItem>
              <MenuSeparator />
              <div className={styles.menuLabel}>Status</div>
              <MenuItem active={!menuNode.status} onClick={() => (setMenu(null), engine.setStatus(menuNode.id, null))}>
                <span className={styles.statusDot} /> None
              </MenuItem>
              {STATUSES.map((status) => (
                <MenuItem key={status} active={menuNode.status === status} onClick={() => (setMenu(null), engine.setStatus(menuNode.id, status))}>
                  <span className={styles.statusDot} style={{ background: STATUS_COLOURS[status] }} /> {STATUS_LABELS[status]}
                </MenuItem>
              ))}
              <MenuSeparator />
            </>
          ) : null}
          {menuNode.children.length > 0 ? (
            <>
              <MenuItem shortcut="Space" onClick={() => (setMenu(null), engine.toggleCollapse(menuNode.id))}>
                {menuNode.collapsed ? 'Expand' : 'Collapse'}
              </MenuItem>
              <MenuItem onClick={() => (setMenu(null), engine.collapseAllChildren(menuNode.id))}>Collapse all children</MenuItem>
              <MenuSeparator />
            </>
          ) : null}
          <MenuItem onClick={() => (setMenu(null), setNoteFor(menuNode.id))}>
            {editable ? (menuNode.description ? 'Edit note' : 'Add note') : 'View note'}
          </MenuItem>
          <MenuItem onClick={() => (setMenu(null), setPropertiesFor(menuNode.id))}>Properties…</MenuItem>
          <MenuSeparator />
          {editable ? (
            <>
              <MenuItem onClick={() => (setMenu(null), setGenerateFor(menuNode.id))}>
                {menuIsRoot ? 'Create storymap from mindmap…' : 'Create storymap from this branch…'}
              </MenuItem>
              <MenuItem onClick={() => (setMenu(null), importAsBranch(menuNode.id))}>Import as branch…</MenuItem>
            </>
          ) : null}
          {trial ? null : <MenuItem onClick={() => (setMenu(null), exportJson(menuNode, menuNode.title))}>Export branch (JSON)</MenuItem>}
          {editable && !menuIsRoot ? (
            <>
              <MenuSeparator />
              <MenuItem danger shortcut="Del" onClick={() => (setMenu(null), confirmDelete(menuNode.id))}>
                Delete node
              </MenuItem>
            </>
          ) : null}
        </FloatingMenu>
      ) : null}

      {linksFor ? (
        <LinksPopover
          x={linksFor.x}
          y={linksFor.y}
          links={links}
          nodeId={linksFor.nodeId}
          onOpen={async (boardId, cardId) => {
            setLinksFor(null)
            await openStorymap(boardId, cardId)
          }}
        />
      ) : null}

      {noteFor && engine ? (
        <NoteDialog
          title={engine.getNode(noteFor)?.title ?? 'Note'}
          value={engine.getNode(noteFor)?.description ?? ''}
          readOnly={!editable}
          onClose={() => setNoteFor(null)}
          onSave={(note) => {
            engine.setDescription(noteFor, note)
            setNoteFor(null)
          }}
        />
      ) : null}

      {propertiesFor && engine && engine.getNode(propertiesFor) ? (
        <PropertiesDialog
          node={engine.getNode(propertiesFor)!}
          mindmapId={mindmapId}
          root={engine.getRoot()!}
          readOnly={!editable}
          onClose={() => setPropertiesFor(null)}
          onSave={(patch) => {
            engine.setFields(propertiesFor, patch)
            setPropertiesFor(null)
          }}
        />
      ) : null}

      {generateFor && mindmapId && engine ? (
        <GenerateStorymapDialog
          mindmapId={mindmapId}
          nodeId={generateFor}
          root={engine.getRoot()!}
          boards={links.boards.filter((board) => board.nodeId === generateFor && canEditBoard(board.access))}
          onClose={() => setGenerateFor(null)}
          onDone={() => setGenerateFor(null)}
        />
      ) : null}

      <input
        ref={importInput}
        type="file"
        accept=".json,application/json"
        className="visually-hidden"
        tabIndex={-1}
        onChange={(event) => {
          void onImportFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />

      {toast ? (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      ) : null}

      <footer className={styles.footer}>
        <span className={styles.muted}>
          <a className={styles.link} href="https://teamcentric.io" target="_blank" rel="noreferrer noopener">
            Teamcentric
          </a>{' '}
          &copy; 2026
        </span>
        <AppVersion className={styles.muted} />
        <span className={styles.spacer} />
        {links.boards.length > 0 && editable ? <LinkSyncToggles /> : null}
        <MindmapSaveStatus />
        <FeedbackTab />
      </footer>
    </div>
  )
}

/** The card palette, so a node and the cards made from it are the same colour. */
const MINDMAP_COLOUR_OPTIONS = CARD_COLOURS.filter((colour) => colour !== 'default').map((colour) => ({
  value: CARD_COLOUR_HEX[colour],
  label: CARD_COLOUR_LABELS[colour],
  colour: CARD_COLOUR_HEX[colour],
}))

function MindmapSaveStatus() {
  const status = useMindmapStore((state) => state.saveStatus)
  const error = useMindmapStore((state) => state.saveError)
  const lastSavedAt = useMindmapStore((state) => state.lastSavedAt)
  const access = useMindmapStore((state) => state.access)
  if (!canEditBoard(access)) return <span className={styles.muted}>View only</span>
  if (status === 'conflict') {
    return (
      <span className={styles.saveError} role="alert">
        Changed elsewhere — your latest edits are not saved.{' '}
        <Button onClick={() => void useMindmapStore.getState().reload()}>Load latest</Button>
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className={styles.saveError} role="alert">
        Save failed: {error}{' '}
        <Button onClick={() => useMindmapStore.getState().retry()}>Retry</Button>
      </span>
    )
  }
  const text =
    status === 'saving' ? 'Saving…' : status === 'pending' ? 'Changed' : lastSavedAt ? `Saved ${new Date(lastSavedAt).toLocaleString()}` : ''
  return (
    <span className={styles.muted} data-testid="mindmap-save-status">
      {text}
    </span>
  )
}

export function MenuItem({
  children,
  onClick,
  shortcut,
  danger,
  active,
}: {
  children: ReactNode
  onClick: () => void
  shortcut?: string
  danger?: boolean
  active?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`${styles.menuItem} ${danger ? styles.menuDanger : ''} ${active ? styles.menuActive : ''}`}
      onClick={onClick}
    >
      <span className={styles.menuText}>{children}</span>
      {shortcut ? <span className={styles.shortcut}>{shortcut}</span> : null}
    </button>
  )
}

function MenuSeparator() {
  return <div className={styles.menuSeparator} role="separator" />
}

/** A menu at the pointer, moved up and left once measured so it stays inside the window. */
function FloatingMenu({ x, y, label, children }: { x: number; y: number; label?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ left: x, top: y })
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const { offsetWidth: width, offsetHeight: height } = element
    setPosition({
      left: x + width > window.innerWidth - 8 ? Math.max(8, x - width) : x,
      top: Math.max(8, Math.min(y, window.innerHeight - height - 8)),
    })
  }, [x, y])
  return (
    <div ref={ref} className={styles.contextMenu} data-popup role="menu" aria-label={label} style={position}>
      {children}
    </div>
  )
}

function popupPosition(rect: { x: number; y: number; width: number } | null): { left: number; top: number } {
  if (!rect) return { left: 16, top: 16 }
  return { left: rect.x + rect.width + 10, top: rect.y }
}

function InlineNodeEditor({
  rect,
  value,
  onDone,
}: {
  rect: { x: number; y: number; width: number; height: number; fontSize: number } | null
  value: string
  onDone: (text: string | null) => void
}) {
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  const finished = useRef(false)
  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])
  if (!rect) return null
  const finish = (text: string | null) => {
    if (finished.current) return
    finished.current = true
    onDone(text)
  }
  return (
    <input
      ref={ref}
      className={styles.nodeEditor}
      aria-label="Node text"
      maxLength={MINDMAP_LIMITS.title}
      style={{ left: rect.x, top: rect.y, width: Math.max(rect.width, 120), height: rect.height, fontSize: rect.fontSize }}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => finish(draft)}
      onKeyDown={(event) => {
        event.stopPropagation()
        if (event.key === 'Enter') {
          event.preventDefault()
          finish(draft)
        } else if (event.key === 'Escape') {
          event.preventDefault()
          finish(null)
        }
      }}
    />
  )
}

function NoteDialog({
  title,
  value,
  readOnly,
  onClose,
  onSave,
}: {
  title: string
  value: string
  readOnly: boolean
  onClose: () => void
  onSave: (note: string) => void
}) {
  const [draft, setDraft] = useState(value)
  return (
    <Dialog
      open
      title={`Note — ${title}`}
      onClose={onClose}
      onSubmit={readOnly ? undefined : () => onSave(draft)}
      footer={
        readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </>
        )
      }
    >
      <TextArea
        aria-label="Note"
        data-autofocus
        rows={10}
        value={draft}
        readOnly={readOnly}
        maxLength={MINDMAP_LIMITS.description}
        placeholder="Enter note…"
        onChange={(event) => setDraft(event.target.value)}
      />
    </Dialog>
  )
}

/** The fields a node shares with cards; tags and placement are typed new or picked from the project's labels. */
function PropertiesDialog({
  node,
  mindmapId,
  root,
  readOnly,
  onClose,
  onSave,
}: {
  node: MindmapNode
  mindmapId: string | null
  root: MindmapNode
  readOnly: boolean
  onClose: () => void
  onSave: (patch: Partial<NodeCore>) => void
}) {
  const [draft, setDraft] = useState<NodeCore>(node)
  const projectLabels = useProjectLabels(mindmapId)
  // The project's labels as last saved, plus those set on this mindmap since.
  const labels: ProjectLabels = mergeLabels([projectLabels, nodeLabels(mindmapNodes(root))])
  const targets = mindmapNodes(root)
    .filter((other) => other.id !== node.id)
    .map((other) => ({ id: other.id, label: other.title }))
  const { placement } = draft
  return (
    <Dialog
      open
      title={`Properties — ${node.title}`}
      onClose={onClose}
      onSubmit={
        readOnly
          ? undefined
          : () =>
              onSave({
                type: draft.type,
                status: draft.status,
                owner: draft.owner.trim(),
                icon: draft.icon,
                tags: draft.tags,
                placement: { group: placement.group.trim(), column: placement.column.trim(), swimlane: placement.swimlane.trim(), row: placement.row.trim() },
                references: draft.references,
                relationships: draft.relationships,
              })
      }
      footer={
        readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </>
        )
      }
    >
      <NodeCoreFields node={draft} labels={labels} targets={targets} readOnly={readOnly} onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))} />
    </Dialog>
  )
}

function LinksPopover({
  x,
  y,
  links,
  nodeId,
  onOpen,
}: {
  x: number
  y: number
  links: MindmapLinks
  nodeId: string
  onOpen: (boardId: string, cardId: string) => void
}) {
  const cards = links.cards.filter((card) => card.nodeId === nodeId)
  const boardName = new Map(links.boards.map((board) => [board.id, board.name]))
  return (
    <FloatingMenu x={x} y={y} label="Linked cards">
      <div className={styles.menuLabel}>Cards from this node</div>
      {cards.map((card) => (
        <MenuItem key={card.cardId} onClick={() => onOpen(card.boardId, card.cardId)}>
          {card.title} <span className={styles.muted}>· {boardName.get(card.boardId) ?? 'Board'}</span>
        </MenuItem>
      ))}
    </FloatingMenu>
  )
}
